/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { Keys } from '#utils/UNSAFE_keys';
import type { SelectionDetail } from '#UNSAFE_Collection';
import type {
  CellPadding,
  SortCriterionDetail,
  TableHeaderRendererContext,
  TableSelectionDetail
} from '#UNSAFE_TableView';

import { useCallback } from 'preact/hooks';
import { FormContext } from '#hooks/UNSAFE_useFormContext';
import { SortableContext } from '#hooks/UNSAFE_useSortableContext';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { isSameKey } from '#utils/UNSAFE_keys';
import { SelectorAll } from '#UNSAFE_SelectorAll';
import { SortControl } from '#UNSAFE_SortControl';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { justifyContentStyles } from '#PRIVATE_TableCommon/themes/Common.css';
import { preventBrowserTooltipProps } from '#PRIVATE_TableCommon/utils/TooltipUtils';
import { tableHeaderCellTextStyles, tableHeaderSortContainerStyles } from './themes/Table.css';
import { tableHeaderCellStyles } from './themes/TableHeaderCellStyles.css';
import { getFormTextAlign, getHeaderFooterCellClassArray } from './utils/TableThemeUtils';

/**
 * Props for the TableHeaderCell Component
 */
type TableHeaderCellProps<K, C> = {
  tableId: string;
  columnKey: C;
  columnIndex: number;
  isFinalColumnIndex: boolean;
  isFirstLogicalColumnIndex: boolean;
  isFinalLogicalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSelected: boolean;
  isNextColumnSelected: boolean;
  isPreviousColumnSelected: boolean;
  isSticky: boolean;
  isColumnSelectable: boolean;
  isRowSelectionEnabled: boolean;
  isShowFocusRing: boolean;
  hasTabIndex: boolean;
  isTabbableMode: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  isRtl: boolean;
  isAccColumnHeader: boolean;
  isHighContrast: boolean;
  headerText?: string;
  selectedRowKeys?: Keys<K>;
  stickyTopOffset?: number;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isActiveStyle: boolean;
  isHoverStyle: boolean;
  isPreviousCellActiveStyle: boolean;
  isPreviousCellHoverStyle: boolean;
  focusTarget?: 'cell' | 'content';
  renderer?: (context: TableHeaderRendererContext<C>) => ComponentChildren;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  isShowSortIcon: boolean;
  sortable?: 'enabled' | 'disabled';
  sortDirection?: 'ascending' | 'descending' | 'unsorted';
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  padding?: CellPadding;
  tooltip?: 'enabled' | 'disabled';
  horizontalAlignment?: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment?: 'top' | 'center' | 'bottom';
};

/**
 * The internal component used to render a single cell in a TableHeaderRow.
 */
export function TableHeaderCell<K, C>({
  tableId,
  columnKey,
  columnIndex,
  isFinalColumnIndex,
  isFirstLogicalColumnIndex,
  isFinalLogicalColumnIndex,
  hasVerticalGridlines,
  isSelected,
  isNextColumnSelected,
  isPreviousColumnSelected,
  isSticky,
  isColumnSelectable,
  isRowSelectionEnabled,
  selectedRowKeys = { all: false, keys: new Set<K>() },
  isShowFocusRing,
  hasTabIndex,
  isTabbableMode,
  headerText,
  stickyTopOffset = 0,
  focusTarget = 'cell',
  renderer,
  onSelectionChange,
  isShowSortIcon,
  sortable = 'disabled',
  sortDirection = 'unsorted',
  onSortCriterionChange,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isActiveStyle,
  isHoverStyle,
  isPreviousCellActiveStyle,
  isPreviousCellHoverStyle,
  isRtl,
  isAccColumnHeader,
  isHighContrast,
  padding,
  tooltip,
  horizontalAlignment = 'start',
  verticalAlignment = 'center'
}: TableHeaderCellProps<K, C>) {
  const browser = getClientHints().browser;
  const supportsDefaultTooltip = tooltip !== 'disabled';
  const supportsAutoFocus = focusTarget === 'content';
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const cellClasses = classNames(
    getHeaderFooterCellClassArray({
      isHeader: true,
      isActiveStyle,
      isHoverStyle,
      isPreviousCellActiveStyle,
      isPreviousCellHoverStyle,
      isFirstColumnIndex: columnIndex === 0 || isFirstLogicalColumnIndex,
      isFinalColumnIndex: isFinalColumnIndex || isFinalLogicalColumnIndex,
      hasVerticalGridlines,
      isSelected,
      isNextColumnSelected,
      isPreviousColumnSelected,
      isSticky,
      isShowFocusRing,
      isStickyStartEdge,
      isStickyEndEdge,
      hasStartEdge,
      hasEndEdge,
      isRtl,
      horizontalAlignment,
      verticalAlignment,
      isHighContrast,
      padding
    })
  );

  let selectorAllState: 'all' | 'none' | 'partial' | 'partial-all';
  if (isSameKey(selectedRowKeys, { all: true, deletedKeys: new Set<K>() })) {
    selectorAllState = 'all';
  } else if (isSameKey(selectedRowKeys, { all: false, keys: new Set<K>() })) {
    selectorAllState = 'none';
  } else {
    selectorAllState = 'partial';
  }

  const handleSelectorChange = (detail: SelectionDetail<K>) => {
    if (onSelectionChange != null) {
      onSelectionChange(
        {
          value: {
            row: detail.value,
            column: { all: false, keys: new Set<C>() }
          },
          target: detail.target
        },
        false
      );
    }
  };

  const selectorRenderer = isRowSelectionEnabled
    ? () => (
        <SelectorAll
          aria-label={translations.collection_selectAllRows()}
          onChange={handleSelectorChange}
          selected={selectorAllState}
        />
      )
    : undefined;

  const handleSortGesture = useCallback(() => {
    if (onSortCriterionChange != null) {
      const newDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
      onSortCriterionChange([{ key: columnKey, sortDirection: newDirection }]);
    }
  }, [columnKey, sortDirection, onSortCriterionChange]);

  const headerCellRendererProps = {
    key: columnKey,
    headerText: headerText,
    sortable: sortable,
    sortDirection: sortDirection,
    selector: selectorRenderer,
    isTabbable: isTabbableMode
  };

  const getHeaderCellRenderer = useCallback(() => {
    if (renderer != null) {
      return renderer;
    }
    return ({ headerText, sortable, sortDirection }: TableHeaderRendererContext<C>) => {
      const text =
        headerText != null ? (
          <div class={tableHeaderCellTextStyles.base}>
            <span class={tableHeaderCellStyles.defaultRendererSpan}>{headerText}</span>
          </div>
        ) : undefined;
      if (sortable === 'enabled' || sortDirection !== 'unsorted') {
        const isIconFirst =
          horizontalAlignment === 'end' ||
          (!isRtl && horizontalAlignment === 'right') ||
          (isRtl && horizontalAlignment === 'left');

        const containerClasses = classNames([
          tableHeaderSortContainerStyles.base,
          justifyContentStyles[horizontalAlignment]
        ]);
        return (
          <div class={containerClasses}>
            {isIconFirst && <SortControl />}
            {text}
            {!isIconFirst && <SortControl />}
          </div>
        );
      }
      return text;
    };
  }, [renderer, horizontalAlignment, isRtl]);

  const getAriaSelected = () => {
    if (isSelected) {
      return { 'aria-selected': true };
    }
    return isColumnSelectable ? { 'aria-selected': false } : undefined;
  };

  const getAriaSort = () => {
    if (sortDirection === 'ascending' || sortDirection === 'descending') {
      return { 'aria-sort': sortDirection };
    }
    return undefined;
  };

  return (
    <SortableContext.Provider
      value={{ isRendered: isShowSortIcon, sortable, sortDirection, onSort: handleSortGesture }}>
      <th
        aria-colindex={columnIndex + 1}
        {...getAriaSelected()}
        {...getAriaSort()}
        class={cellClasses}
        style={`top:${stickyTopOffset}px;`}
        tabIndex={hasTabIndex ? 0 : -1}
        role={isAccColumnHeader ? 'columnheader' : 'gridcell'}
        data-oj-cell-type={'header'}
        data-oj-column-key={columnKey}
        data-oj-table-focusable={tableId}
        data-oj-table-header={tableId}
        data-oj-table-tooltip={supportsDefaultTooltip ? 'enabled' : undefined}
        data-oj-table-auto-focus={supportsAutoFocus ? 'enabled' : undefined}
        {...(supportsDefaultTooltip && browser === 'safari' ? preventBrowserTooltipProps : {})}>
        <FormContext.Provider value={{ textAlign: getFormTextAlign(horizontalAlignment, isRtl) }}>
          {getHeaderCellRenderer()(headerCellRendererProps)}
        </FormContext.Provider>
      </th>
    </SortableContext.Provider>
  );
}
